import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { IPartnership, PartnershipStatus } from 'identity-webapi-client';
import { Subscription } from 'rxjs';
import { PartnershipService } from 'src/app/services';
import { PartnershipActionConfirmationDialogComponent, TransitionPartnershipRequest } from 'src/app/views';

@Component({
  selector: 'partnership-card',
  templateUrl: './partnership-card.component.html',
  styleUrls: ['./partnership-card.component.less']
})
export class PartnershipCardComponent implements OnDestroy {

  @Input() item: IPartnership | undefined;
  @Input() companyId: string | undefined;
  @Input() loading: boolean = false;
  @Input() readonly: boolean = false;
  @Input() selected: boolean = false;
  @Output() transitionPartnershipEvent: EventEmitter<TransitionPartnershipRequest> = new EventEmitter();

  partnershipStatus = PartnershipStatus;
  transitionStatus: PartnershipStatus | undefined;

  _subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private partnershipService: PartnershipService
  ) { }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      this._subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      })
    }
  }

  viewPartnership(partnership: IPartnership) {
    this.router.navigate([`/admin/partnerships/${partnership.id}`]);
  }

  cancelPartnershipRequest(partnership: IPartnership) {
    this.transitionStatus = PartnershipStatus.Cancelled;
    this.transitionPartnership(partnership, PartnershipStatus.Cancelled);
  }
  
  terminatePartnership(partnership: IPartnership) {
    this.loading = true;
    this.transitionStatus = PartnershipStatus.Terminated;
    const dialogRef = this.dialog.open(PartnershipActionConfirmationDialogComponent, {
      width: '300px',
      data: { partnerName: this.getOtherCompanyName(partnership, this.companyId!), request: false }
    });

    this._subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.transitionPartnership(partnership, PartnershipStatus.Terminated);
      } else {
        this.snackBar.open(`Terminate Partnership Cancelled!`, 'OK', { duration: 2000 });
        this.loading = false;
        this.transitionStatus = undefined;
      }
    }));
  }

  reconnectPartnership(partnership: IPartnership) {
    this.transitionStatus = PartnershipStatus.Reconnected;
    this.transitionPartnership(partnership, PartnershipStatus.Reconnected);
  }

  transitionPartnership(partnership: IPartnership, newStatus: PartnershipStatus) {
    this.transitionPartnershipEvent.emit({partnership: partnership, newStatus: newStatus});
  }

  isInitiater(partnership: IPartnership | undefined): boolean {
    return partnership?.requesterCompanyId === this.companyId;
  }

  getCompanyName(partnership: IPartnership | undefined): string | undefined {
    if (partnership?.requesterCompanyId === this.companyId) {
      return partnership?.requesteeCompany?.name;
    }
    return partnership?.requesterCompany?.name;
  }

  getOtherCompanyName(partnership: IPartnership | null, companyId: string): string | undefined {
    return this.partnershipService.getOtherCompanyName(partnership, companyId);
  }

  getDateText(partnership: IPartnership | undefined): string | undefined {
    switch(partnership?.status) {
      case PartnershipStatus.Terminated:
        return 'terminated on';
      case PartnershipStatus.Requested:
        return this.isInitiater(partnership) ? 
          `requested on` : 
          `requested by ${partnership.requesterCompany?.name} on`;
      case PartnershipStatus.Approved:
      case PartnershipStatus.Reconnected:
        return 'partnered on';
      case PartnershipStatus.Cancelled:
      case PartnershipStatus.Denied:
      default:
        return "";
    }
  }
}
